import React, { useState } from 'react';
import { Compass } from 'lucide-react';
import { useCandidates } from '../../lib/useCandidates';
import { VerdictChip, EXPLOSION_STYLE } from './Verdict';
import LivePrice from './LivePrice';

const DirCell = ({ v }) => {
    const d = v.direction;
    const cls = d === 'long' ? 'text-emerald-300' : d === 'short' ? 'text-rose-300' : 'text-slate-400';
    const conf = v.confidence || 0;
    return (
        <div className="flex items-center gap-2">
            <span className={`font-bold text-sm ${cls}`}>{v.direction_tr}</span>
            {d !== 'neutral' && (
                <span className="flex items-center gap-1 text-[11px] text-slate-300">
                    <span className="h-1.5 w-16 rounded bg-slate-700 overflow-hidden"><span className={`block h-full ${d === 'long' ? 'bg-emerald-400' : 'bg-rose-400'}`} style={{ width: `${Math.min(100, conf)}%` }} /></span>
                    %{conf}
                </span>
            )}
        </div>
    );
};

/** Diğer coinler — yön ihtimali: everything that is NOT a candidate, in one plain table. */
export default function OtherCoinsTable({ rows, onSelect, tickPrices }) {
    const { others } = useCandidates(rows);
    const [showAll, setShowAll] = useState(false);
    const limit = showAll ? 100 : 12;
    return (
        <div data-testid="other-coins" className="border border-slate-700 bg-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-700 flex items-center gap-x-3 gap-y-1 flex-wrap">
                <Compass className="h-4 w-4 text-slate-300" />
                <span className="text-sm font-bold text-slate-50">Diğer coinler — yön ihtimali</span>
                <span className="text-xs text-slate-400">Patlama adayı değiller. Sadece hangi yöne eğilimli oldukları ve ne kadar güvenle. Hepsi için karar: <b className="text-slate-200">BEKLE</b> (koşmuş olanlar: KOVALAMA YAPMA).</span>
                <span className="ml-auto text-xs text-slate-400" data-testid="other-coins-count">{others.length} coin</span>
            </div>
            <div className="divide-y divide-slate-700/60">
                <div className="hidden md:grid grid-cols-12 px-4 py-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                    <div className="col-span-2">Coin</div><div className="col-span-2">Fiyat · bugün</div><div className="col-span-3">Yön · güven</div>
                    <div className="col-span-2">Patlama ihtimali</div><div className="col-span-3">Karar</div>
                </div>
                {others.slice(0, limit).map(({ v, row: r, symbol }) => {
                    const ch = Number(r.change_pct_24h || 0);
                    return (
                        <button key={symbol} type="button" onClick={() => onSelect?.(symbol)} data-testid={`other-row-${symbol}`}
                                className="w-full text-left grid grid-cols-2 md:grid-cols-12 gap-x-3 gap-y-1 px-4 py-2.5 hover:bg-slate-700/40 items-center">
                            <div className="md:col-span-2 font-mono font-bold text-slate-50">{symbol.replace('USDT', '')}</div>
                            <div className="md:col-span-2 font-mono text-xs text-slate-200 flex items-center gap-2 justify-end md:justify-start">
                                <LivePrice symbol={symbol} tick={tickPrices?.[symbol]} fallbackPrice={r.price} />
                                <span className={ch >= 0 ? 'text-emerald-300' : 'text-rose-300'}>{ch >= 0 ? '+' : ''}{ch.toFixed(1)}%</span>
                            </div>
                            <div className="md:col-span-3"><DirCell v={v} /></div>
                            <div className="md:col-span-2 text-sm" title={v.explosion_basis_tr || ''}><b className={EXPLOSION_STYLE[v.explosion]}>{v.explosion_label_tr || v.explosion_tr}</b></div>
                            <div className="col-span-2 md:col-span-3 flex items-center gap-2 flex-wrap">
                                <VerdictChip v={v} testId={`other-verdict-${symbol}`} />
                                <span className="text-[11px] text-slate-400 leading-snug">{v.why_tr}</span>
                            </div>
                        </button>
                    );
                })}
                {others.length === 0 && <div className="px-4 py-4 text-sm text-slate-400">Tüm coinler aday listesinde.</div>}
            </div>
            {others.length > limit && (
                <button type="button" data-testid="other-coins-show-all" onClick={() => setShowAll(true)}
                        className="w-full border-t border-slate-700 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-700/40">
                    Tümünü göster (+{others.length - limit} coin)
                </button>
            )}
        </div>
    );
}
